// src/pages/Statistics.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Panel from "../central/Panel";
import Button from "../central/Button";
// import { col, ref } from "../lib/fw";
import { getDocs } from "firebase/firestore";
import { paths } from "../lib/paths";
import PageLoader from "../components/PageLoader";
import { useSeason } from "../context/SeasonContext";
import SeasonSelector from "../components/SeasonSelector";
import PlayerProfileModal from "../components/PlayerProfileModal";

const cellStyle = { width: "auto", whiteSpace: "normal", overflowWrap: "break-word" };
const headCellStyle = { padding: "0.25rem", ...cellStyle };

// Table columns (label shown in header; title used for tooltip + legend)
const COLUMNS = [
  { field: "position", label: "POS", title: "Position" },
  { field: "totalPoints", label: "PTS", title: "Total points" },
  { field: "ptsPerApp", label: "P/A", title: "Points per appearance", decimals: 1 },
  { field: "appearances", label: "APS", title: "Appearances" },
  { field: "starts", label: "1XI", title: "Starting XI selections" },
  { field: "goals", label: "GLS", title: "Goals" },
  { field: "assists", label: "AST", title: "Assists" },
  { field: "ga", label: "G+A", title: "Goals + assists" },
  { field: "gaPerApp", label: "G+A/A", title: "Goals + assists per appearance", decimals: 2 },
  { field: "cleanSheets", label: "CS", title: "Clean sheets" },
  { field: "gkSaveBonus", label: "SAV", title: "GK 3+ save bonuses" },
  { field: "bonusPoints", label: "BON", title: "Bonus points (best players)" },
  { field: "yellowCards", label: "YC", title: "Yellow cards" },
  { field: "form", label: "FORM", title: "Points in the last 5 gameweeks" },
];

// Columns where the league-best value gets highlighted
const LEADER_FIELDS = [
  "totalPoints",
  "ptsPerApp",
  "appearances",
  "starts",
  "goals",
  "assists",
  "ga",
  "gaPerApp",
  "cleanSheets",
  "gkSaveBonus",
  "bonusPoints",
];

// Tiny inline bar chart for recent gameweek points
function FormBars({ values }) {
  if (!Array.isArray(values) || values.length === 0) return "–";
  const max = Math.max(...values, 1);
  const barW = 6;
  const gap = 2;
  const h = 18;
  const w = values.length * (barW + gap) - gap;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={`Last ${values.length} gameweeks: ${values.join(", ")}`}
      style={{ verticalAlign: "middle" }}
    >
      {values.map((v, i) => {
        const bh = Math.max(2, (Math.max(v, 0) / max) * h);
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={h - bh}
            width={barW}
            height={bh}
            rx={1}
            fill="currentColor"
            opacity={v > 0 ? 0.75 : 0.25}
          >
            <title>{String(v)}</title>
          </rect>
        );
      })}
    </svg>
  );
}


export default function Statistics() {
  const { leagueID } = useParams();
  const { selectedSeasonID, availableSeasons, isCurrentSeason } = useSeason();

  const [players, setPlayers] = useState([]);
  const [gameweeks, setGameweeks] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortField, setSortField] = useState("totalPoints");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState("ALL");
  const [minApps, setMinApps] = useState(1);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // LOAD PLAYERS
  useEffect(() => {
    if (!leagueID) return;
    let cancelled = false;
    setPlayers([]);
    (async () => {
      const snap = await getDocs(paths.leaguePlayers(leagueID, selectedSeasonID));
      if (cancelled) return;
      const list = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || "Player",
          position: data.position,
          pointsHistory: data.pointsHistory || {},
        };
      });
      setPlayers(list);
    })();
    return () => { cancelled = true; };
  }, [leagueID, selectedSeasonID]);

  // LOAD GAMEWEEKS
  useEffect(() => {
    if (!leagueID) return;
    let cancelled = false;
    setGameweeks([]);
    (async () => {
      const snap = await getDocs(paths.leagueGameweeks(leagueID, selectedSeasonID));
      if (cancelled) return;
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.id !== "meta");

      const sorted = rows.sort(
        (a, b) => (Number(a.number) || 0) - (Number(b.number) || 0)
      );
      setGameweeks(sorted.map((r) => r.id));
    })();
    return () => { cancelled = true; };
  }, [leagueID, selectedSeasonID]);

  // LOAD MATCHES + AGGREGATE PLAYER STATS
  useEffect(() => {
    if (!leagueID || players.length === 0 || gameweeks.length === 0) return;

    let cancelled = false;
    (async () => {
      setLoading(true);

      const map = {};
      for (const p of players) {
        map[p.id] = {
          id: p.id,
          name: p.name,
          position: p.position,
          pointsHistory: p.pointsHistory || {},
          totalPoints: Object.values(p.pointsHistory || {}).reduce(
            (s, x) => s + Number(x || 0),
            0
          ),
          appearances: 0,
          starts: 0,
          goals: 0,
          assists: 0,
          cleanSheets: 0,
          gkSaveBonus: 0,
          bonusPoints: 0,
          yellowCards: 0,
        };
      }

      for (const gwID of gameweeks) {
        const snap = await getDocs(paths.gwMatches(leagueID, gwID, selectedSeasonID));
        for (const m of snap.docs) {
          const match = m.data() || {};
          const appearanceData = match.appearanceData || {};

          // APPEARANCES
          for (const pid in appearanceData) {
            if (!map[pid]) continue;
            if (appearanceData[pid].appeared) map[pid].appearances += 1;
          }

          // STARTERS (correct field)
          if (match.starters && typeof match.starters === "object") {
            for (const pid in match.starters) {
              if (match.starters[pid] === true && map[pid]) {
                map[pid].starts += 1;
              }
            }
          }

          // CLEAN SHEETS
          if (match.cleanSheet) {
            for (const pid in appearanceData) {
              if (appearanceData[pid].appeared && map[pid]) {
                map[pid].cleanSheets += 1;
              }
            }
          }

          // GK SAVE BONUS
          const assigns = match.infractionAssignments || {};
          for (const key in assigns) {
            if (key === "GK 3+ Save Bonus") {
              const pid = assigns[key];
              if (map[pid]) map[pid].gkSaveBonus += 1;
            }
          }


          // GOALS + ASSISTS
          const goals = match.goals || [];
          goals.forEach((g) => {
            const scorer = g.scorer;
            const assist = g.assist;

            // Goals scored
            if (scorer && map[scorer]) {
              map[scorer].goals += 1;
            }

            // Assists (skip empty)
            if (
              assist &&
              typeof assist === "string" &&
              assist.trim() !== "" &&
              map[assist]
            ) {
              map[assist].assists += 1;
            }
          });

          // BONUS POINTS (ranked: 1 → 6 pts, 2 → 4 pts, 3 → 2 pts)
          if (match.bestPlayers && typeof match.bestPlayers === "object") {
            const bp = match.bestPlayers;

            if (bp["1"] && map[bp["1"]]) map[bp["1"]].bonusPoints += 3;
            if (bp["2"] && map[bp["2"]]) map[bp["2"]].bonusPoints += 2;
            if (bp["3"] && map[bp["3"]]) map[bp["3"]].bonusPoints += 1;
          }
        }

        // After processing all matches for a GW, aggregate yellow cards from gw-level playerPoints
        const gwPlayerPointsSnap = await getDocs(paths.gwPlayerPoints(leagueID, gwID, selectedSeasonID));
        for (const doc of gwPlayerPointsSnap.docs) {
          const pid = doc.id;
          if (!map[pid]) continue;
          const breakdown = doc.data()?.breakdown || {};
          if ("Yellow Card" in breakdown) {
            map[pid].yellowCards += 1;
          }
        }
      }

      if (cancelled) return;

      const lastGWs = gameweeks.slice(-5);
      const arr = Object.values(map).map((p) => ({
        ...p,
        ga: p.goals + p.assists,
        form: lastGWs.map((gw) => Number(p.pointsHistory?.[gw] || 0)),
      }));
      arr.sort((a, b) => b.totalPoints - a.totalPoints);

      setStats(arr);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [leagueID, players, gameweeks, selectedSeasonID]);

  const onSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // ---------- derived data ----------
  // Competition ranking by total points (independent of the active sort)
  const rankById = useMemo(() => {
    const byPts = [...stats].sort((a, b) => b.totalPoints - a.totalPoints);
    const ranks = {};
    byPts.forEach((p, i) => {
      ranks[p.id] =
        i > 0 && p.totalPoints === byPts[i - 1].totalPoints
          ? ranks[byPts[i - 1].id]
          : i + 1;
    });
    return ranks;
  }, [stats]);

  // Per-appearance values only count once a player has enough appearances
  const withEligibility = useMemo(
    () =>
      stats.map((p) => {
        const ok = p.appearances >= Math.max(1, minApps);
        return {
          ...p,
          ptsPerApp: ok ? p.totalPoints / p.appearances : null,
          gaPerApp: ok ? p.ga / p.appearances : null,
        };
      }),
    [stats, minApps]
  );

  // Column maxima (over everyone, not just the filtered rows) for highlighting
  const maxima = useMemo(() => {
    const out = {};
    LEADER_FIELDS.forEach((f) => {
      let max = 0;
      withEligibility.forEach((p) => {
        const v = p[f];
        if (typeof v === "number" && v > max) max = v;
      });
      out[f] = max;
    });
    return out;
  }, [withEligibility]);

  const positionOptions = useMemo(
    () =>
      Array.from(
        new Set(stats.map((p) => (p.position || "").toUpperCase()).filter(Boolean))
      ),
    [stats]
  );

  const leaders = useMemo(() => {
    const pick = (field, label, fmt = (v) => v) => {
      const max = maxima[field];
      if (!max) return null;
      const top = withEligibility.filter((p) => p[field] === max);
      return {
        label,
        value: fmt(max),
        name: top[0].name,
        extra: top.length > 1 ? top.length - 1 : 0,
      };
    };
    return [
      pick("totalPoints", "Most points"),
      pick("goals", "Top scorer"),
      pick("assists", "Most assists"),
      pick("bonusPoints", "Most bonus"),
    ].filter(Boolean);
  }, [withEligibility, maxima]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = withEligibility.filter((p) => {
      if (posFilter !== "ALL" && (p.position || "").toUpperCase() !== posFilter) return false;
      if (q && !(p.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
    return rows.sort((a, b) => {
      const A = a[sortField];
      const B = b[sortField];
      // empty values (not enough appearances) always sink to the bottom
      const aNil = A === null || A === undefined;
      const bNil = B === null || B === undefined;
      if (aNil || bNil) return aNil === bNil ? 0 : aNil ? 1 : -1;
      let cmp;
      if (typeof A === "string" || typeof B === "string") {
        cmp = String(A).localeCompare(String(B));
      } else if (Array.isArray(A)) {
        cmp = A.reduce((s, x) => s + x, 0) - B.reduce((s, x) => s + x, 0);
      } else {
        cmp = A - B;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [withEligibility, search, posFilter, sortField, sortDir]);

  const fmtCell = (col, p) => {
    const v = p[col.field];
    if (col.field === "form") return <FormBars values={v} />;
    if (v === null || v === undefined) return "–";
    if (col.decimals) return v.toFixed(col.decimals);
    return v;
  };

  const chipStyle = (active) => ({
    padding: "2px 10px",
    borderRadius: 999,
    border: "1px solid #bbb",
    background: active ? "#222" : "#fff",
    color: active ? "#fff" : "#222",
    fontSize: "0.8rem",
    cursor: "pointer",
  });

  return (
    <>
      <div className="p-4">
        <SeasonSelector />

        {loading ? (
          <PageLoader message="Loading statistics…" />
        ) : (
          <Panel maxWidth="1000px">
            {/* Leaders strip */}
            {leaders.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                {leaders.map((l) => (
                  <div
                    key={l.label}
                    style={{
                      border: "1px solid #e2e2e2",
                      borderRadius: 8,
                      padding: "6px 8px",
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ fontSize: "0.7rem", color: "#666" }}>{l.label}</div>
                    <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{l.value}</div>
                    <div style={{ fontSize: "0.78rem", overflowWrap: "anywhere" }}>
                      {l.name}
                      {l.extra > 0 ? ` +${l.extra}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search players"
                aria-label="Search players"
                style={{
                  flex: "1 1 140px",
                  minWidth: 0,
                  padding: "4px 8px",
                  border: "1px solid #bbb",
                  borderRadius: 6,
                  fontSize: "0.85rem",
                }}
              />
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["ALL", ...positionOptions].map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => setPosFilter(pos)}
                    style={chipStyle(posFilter === pos)}
                  >
                    {pos}
                  </button>
                ))}
              </div>
              <label style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 4 }}>
                Min apps (per-app stats)
                <select
                  value={minApps}
                  onChange={(e) => setMinApps(Number(e.target.value))}
                  style={{ border: "1px solid #bbb", borderRadius: 6, padding: "2px 4px" }}
                >
                  {[1, 3, 5, 8].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table
                className="table-auto border-collapse text-sm"
                style={{ minWidth: "100%", tableLayout: "auto" }}
              >
                <thead className="bg-gray-200">
                  <tr>
                    <th style={{ ...headCellStyle, textAlign: "center" }} title="Rank by total points">
                      #
                    </th>
                    <th
                      onClick={() => onSort("name")}
                      className="px-1 py-1 cursor-pointer select-none hover:bg-gray-100"
                      style={{
                        width: "auto",
                        whiteSpace: "normal",
                        position: "sticky",
                        left: 0,
                        zIndex: 20,
                        background: "white",
                        maxWidth: "120px",
                        overflowWrap: "break-word",
                      }}
                    >
                      <div className="flex items-center justify-start gap-1">
                        <span>Player</span>
                        {sortField === "name" && <span>{sortDir === "asc" ? "↑" : "↓"}</span>}
                      </div>
                    </th>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.field}
                        onClick={() => onSort(col.field)}
                        title={col.title}
                        className="px-1 py-1 cursor-pointer select-none text-center hover:bg-gray-100"
                        style={{ width: "auto", whiteSpace: "normal", overflowWrap: "break-word" }}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span>{col.label}</span>
                          {sortField === col.field && <span>{sortDir === "asc" ? "↑" : "↓"}</span>}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={COLUMNS.length + 2} style={{ padding: 12, textAlign: "center", color: "#666" }}>
                        No players match.
                      </td>
                    </tr>
                  )}
                  {visible.map((p, rowIdx) => {
                    const rowBg = rowIdx % 2 === 0 ? "#fff" : "#f7f9fa";
                    return (
                      <tr key={p.id} style={{ background: rowBg }}>
                        <td className="px-0.5 py-0.5" style={{ ...cellStyle, color: "#666", textAlign: "center" }}>
                          {rankById[p.id]}
                        </td>
                        <td
                          className="px-0.5 py-0.5"
                          style={{
                            position: "sticky",
                            left: 0,
                            background: rowBg,
                            zIndex: 4,
                            maxWidth: "120px",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedPlayer(p)}
                            aria-label={`Open ${p.name} profile`}
                            style={{
                              background: "none",
                              border: 0,
                              padding: 0,
                              font: "inherit",
                              color: "inherit",
                              textAlign: "left",
                              cursor: "pointer",
                              textDecoration: "underline",
                              textDecorationStyle: "dotted",
                            }}
                          >
                            {p.name}
                          </button>
                        </td>
                        {COLUMNS.map((col) => {
                          const v = p[col.field];
                          const isLeader =
                            LEADER_FIELDS.includes(col.field) &&
                            typeof v === "number" &&
                            v > 0 &&
                            v === maxima[col.field];
                          return (
                            <td
                              key={col.field}
                              className="px-0.5 py-0.5"
                              style={{
                                ...cellStyle,
                                textAlign: "center",
                                fontWeight: isLeader ? 700 : undefined,
                                background: isLeader ? "rgba(255, 214, 0, 0.28)" : undefined,
                              }}
                            >
                              {fmtCell(col, p)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <details style={{ marginTop: 10, fontSize: "0.78rem", color: "#555" }}>
              <summary style={{ cursor: "pointer" }}>Key</summary>
              <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "2px 12px" }}>
                <span># = rank by points</span>
                {COLUMNS.map((c) => (
                  <span key={c.field}>
                    {c.label} = {c.title}
                  </span>
                ))}
                <span>Highlighted = league-best in that column</span>
              </div>
            </details>
          </Panel>
        )}
      </div>

      <PlayerProfileModal
        open={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        leagueID={leagueID}
        playerID={selectedPlayer?.id}
        player={selectedPlayer}
      />
    </>
  );
}
